import React, { useEffect, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';
import { fetchCategories } from '../../services/apiService';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaChevronRight,
  FaChevronLeft,
  FaTimes,
  FaSignOutAlt
} from 'react-icons/fa';
import {
  HiOutlineShoppingBag,
  HiOutlineViewGrid,
  HiOutlineUser,
  HiOutlineHeart,
  HiOutlineShoppingCart,
} from 'react-icons/hi';
import { pickCategoryName } from '../../utils/categoryUtils';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

function ScrollLock() {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  return null;
}

export default function Sidebar({ solutions, callsToAction, company, resources }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('main');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const sidebarRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { language, t } = useLanguage();
  const { isLoggedIn, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/loginpage');
      setIsSidebarOpen(false);
    } catch (error) {
    }
  };

  useEffect(() => {
    const fetchCategoriesData = async () => {
      try {
        const response = await fetchCategories();
        setCategories(response.data);
      } catch (error) {
      }
    };
    fetchCategoriesData();
  }, []);

  const handleCategoriesClick = () => setCurrentView('categories');

  const handleCategoryClick = (category, close) => {
    const cleanCategoryId = category.category.en.trim();
    sessionStorage.setItem('germanName', category.category.de);
    navigate(`/categories/${cleanCategoryId}/products`);
    close();
    setCurrentView('main');
    setSelectedCategory(null);
  };

  const handleCategoryWithSubcategoriesClick = (category, close) => {
    if (category.subcategories && category.subcategories.length > 0) {
      setSelectedCategory(category);
      setCurrentView('subcategories');
    } else {
      handleCategoryClick(category, close);
    }
  };

  const handleSubcategoryClick = (subcategory, close) => {
    // Parent comes from the category whose subcategory panel is open. Route by
    // the parent-scoped composite id "<parentEn>_<subEn>" so results match the
    // sidebar count and don't leak the same sub-name under other parents.
    const parentEn = selectedCategory?.category?.en?.trim();
    const subEn = subcategory.en.trim();
    const targetId = parentEn ? `${parentEn}_${subEn}` : subEn;
    sessionStorage.setItem('germanName', subcategory.de);
    navigate(`/categories/${targetId}/products`);
    close();
    setCurrentView('main');
    setSelectedCategory(null);
  };

  const handleBackToMain = () => { setCurrentView('main'); setSelectedCategory(null); };
  const handleBackToCategories = () => { setCurrentView('categories'); setSelectedCategory(null); };
  const handlePanelClose = () => { setIsSidebarOpen(false); setCurrentView('main'); setSelectedCategory(null); };

  // --- Shared pieces of the sand look (see the desktop mega-menu in Nav) ---
  const roundBtn = 'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white text-gray-600 ring-1 ring-black/[0.07] shadow-[0_6px_16px_-10px_rgba(17,24,39,0.5)] transition-colors hover:text-brand active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300';
  const sectionLabel = 'mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400';
  const card = 'overflow-hidden rounded-[1.75rem] bg-white p-1.5 ring-1 ring-black/[0.04] shadow-[0_14px_34px_-26px_rgba(17,24,39,0.5)]';
  const row = (active) =>
    `group flex w-full items-center gap-3.5 rounded-[1.25rem] px-2.5 py-2.5 text-left transition-all duration-200 active:scale-[0.98] ${
      active ? 'bg-[#F6F3F0] text-brand' : 'text-gray-800 hover:bg-[#F6F3F0]'
    }`;
  const iconDisc = (active) =>
    `flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
      active ? 'bg-white text-brand ring-1 ring-black/[0.05]' : 'bg-[#F6F3F0] text-gray-500 group-hover:bg-white group-hover:text-brand'
    }`;
  const chevron = (active) =>
    `h-3 w-3 flex-shrink-0 transition-all ${active ? 'text-brand' : 'text-gray-300 group-hover:translate-x-0.5 group-hover:text-brand'}`;

  const ListView = ({ items, onPick, getLabel }) => (
    <div className="relative px-4 py-4">
      <div className={card}>
        {items.map((item, idx) => (
          <button key={item._id || idx} type="button" onClick={() => onPick(item)} className={row(false)}>
            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand/40 transition-colors group-hover:bg-brand" aria-hidden="true" />
            <span className="flex-1 text-[15px] font-semibold">{getLabel(item)}</span>
            <FaChevronRight className={chevron(false)} />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Popover.Panel
      className="fixed inset-0 z-30 custom:hidden"
    >
      {({ close }) => (
        <>
        <ScrollLock />
        <div
          className="absolute inset-0 overflow-y-auto pb-[calc(5rem+env(safe-area-inset-bottom))]"
          ref={sidebarRef}
          style={{ background: 'linear-gradient(160deg, rgb(var(--brand-tint)) 0%, #F6F3F0 30%, #F6F3F0 100%)' }}
        >
          {/* Dot grid, fixed behind the scrolling content. */}
          <div
            aria-hidden="true"
            className="pointer-events-none fixed inset-0"
            style={{ backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)', backgroundSize: '18px 18px' }}
          />

          {/* Header — padded past the status bar / notch so its content is never
              clipped, and stays clear when it sticks on scroll. */}
          <div
            className="sticky top-0 z-10 bg-[#F6F3F0]/85 backdrop-blur-md"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              {currentView !== 'main' ? (
                <button
                  type="button"
                  onClick={currentView === 'categories' ? handleBackToMain : handleBackToCategories}
                  className={roundBtn}
                  aria-label={t('sidebar.back')}
                >
                  <FaChevronLeft className="h-3.5 w-3.5" />
                </button>
              ) : (
                <Link
                  to="/"
                  onClick={() => { close(); handlePanelClose(); }}
                  className="inline-flex overflow-hidden rounded-[1.1rem] bg-white p-1 ring-1 ring-black/[0.05] shadow-[0_10px_24px_-14px_rgba(17,24,39,0.5)]"
                >
                  <img
                    alt="Dreirosen Apotheke"
                    src="/images/resized_newlogo.webp"
                    className="h-11 w-auto rounded-[0.8rem]"
                  />
                </Link>
              )}

              {currentView !== 'main' && (
                <h2 className="min-w-0 flex-1 truncate text-center text-base font-extrabold tracking-[-0.01em] text-gray-900">
                  {currentView === 'categories'
                    ? t('sidebar.categories')
                    : (selectedCategory ? pickCategoryName(selectedCategory.category, language) : t('sidebar.subcategories'))}
                </h2>
              )}

              <button
                type="button"
                className={roundBtn}
                onClick={() => { close(); handlePanelClose(); }}
              >
                <span className="sr-only">{t('sidebar.closeMenu')}</span>
                <FaTimes className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Categories View */}
          {currentView === 'categories' && (
            <ListView
              items={categories}
              onPick={(category) => handleCategoryWithSubcategoriesClick(category, close)}
              getLabel={(category) => pickCategoryName(category.category, language)}
            />
          )}

          {/* Subcategories View */}
          {currentView === 'subcategories' && selectedCategory && (
            <ListView
              items={selectedCategory.subcategories}
              onPick={(subcategory) => handleSubcategoryClick(subcategory, close)}
              getLabel={(subcategory) => pickCategoryName(subcategory, language)}
            />
          )}

          {/* Main View */}
          {currentView === 'main' && (
            <div className="relative space-y-6 px-4 pb-8 pt-4">
              {/* Primary nav */}
              <nav className={card}>
                {[
                  { onClick: () => { navigate('/products'); close(); handlePanelClose(); }, icon: <HiOutlineShoppingBag className="h-5 w-5" />, label: t('sidebar.onlineShop'), href: '/products' },
                  { onClick: handleCategoriesClick, icon: <HiOutlineViewGrid className="h-5 w-5" />, label: t('sidebar.categories') },
                  ...solutions.map((item) => ({
                    onClick: () => { navigate(item.href || '/'); close(); handlePanelClose(); },
                    icon: React.cloneElement(item.icon, { className: "h-5 w-5" }),
                    label: item.name,
                    href: item.href,
                  })),
                ].map((item, idx) => {
                  const isActive = !!item.href && location.pathname === item.href;
                  return (
                    <button key={item.label + idx} type="button" onClick={item.onClick} className={row(isActive)}>
                      <span className={iconDisc(isActive)}>{item.icon}</span>
                      <span className="flex-1 text-[15px] font-semibold">{item.label}</span>
                      <FaChevronRight className={chevron(isActive)} />
                    </button>
                  );
                })}
              </nav>

              {/* Account */}
              {isLoggedIn && (
                <div>
                  <p className={sectionLabel}>{t('sidebar.myAccount')}</p>
                  <nav className={card}>
                    {[
                      { to: '/profilepage', icon: <HiOutlineUser className="h-5 w-5" />, label: t('sidebar.profile') },
                      { to: '/userfavoritespage', icon: <HiOutlineHeart className="h-5 w-5" />, label: t('sidebar.favorites') },
                      { to: '/cartpage', icon: <HiOutlineShoppingCart className="h-5 w-5" />, label: t('sidebar.cart') },
                    ].map((item) => {
                      const isActive = location.pathname === item.to;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => { close(); handlePanelClose(); }}
                          aria-current={isActive ? 'page' : undefined}
                          className={row(isActive)}
                        >
                          <span className={iconDisc(isActive)}>{item.icon}</span>
                          <span className="flex-1 text-[15px] font-medium">{item.label}</span>
                          <FaChevronRight className={chevron(isActive)} />
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              )}

              {/* Company & Resources */}
              <div>
                <p className={sectionLabel}>{t('sidebar.more')}</p>
                <nav className={card}>
                  {[...company, ...resources].map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => { close(); handlePanelClose(); }}
                        aria-current={isActive ? 'page' : undefined}
                        className={row(isActive)}
                      >
                        <span className={`${iconDisc(isActive)} [&>svg]:h-[1.1rem] [&>svg]:w-[1.1rem]`}>{item.icon}</span>
                        <span className="flex-1 text-[14px] font-medium">{item.name}</span>
                        <FaChevronRight className={chevron(isActive)} />
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Auth — the one solid red element in the menu */}
              {!isLoggedIn ? (
                <div className="space-y-3 pt-1">
                  <button
                    type="button"
                    onClick={() => { navigate('/registerpage'); close(); handlePanelClose(); }}
                    className="w-full rounded-full bg-brand py-4 text-[15px] font-semibold text-white shadow-[0_14px_30px_-12px_rgb(var(--brand)/0.6)] transition-all duration-200 hover:bg-brand-hover active:scale-[0.98]"
                  >
                    {t('sidebar.register')}
                  </button>
                  <p className="text-center text-sm text-gray-500">
                    {t('sidebar.alreadyCustomer')}{' '}
                    <button
                      type="button"
                      onClick={() => { navigate('/loginpage'); close(); handlePanelClose(); }}
                      className="font-semibold text-brand transition-colors hover:text-brand-hover"
                    >
                      {t('sidebar.login')}
                    </button>
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { handleLogout(); close(); }}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 text-sm font-semibold text-gray-600 ring-1 ring-black/[0.06] transition-all duration-200 hover:text-brand active:scale-[0.98]"
                >
                  {t('sidebar.logout')}
                  <FaSignOutAlt className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        </>
      )}
    </Popover.Panel>
  );
}
